import GppMaybeOutlinedIcon from "@mui/icons-material/GppMaybeOutlined";
import { Box, FormControl, FormHelperText, InputLabel, alpha } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { DatePicker } from "@syadem/sad-ui";
import { ComponentProps, useEffect, useMemo, useState } from "react";
import { dayjs } from "../../../utils/dayjs";
import { useI18n } from "../../hooks/useI18n";
import { theme } from "../../layout/Theme";
import { StyledInput } from "./StyledInput";
import { Dayjs } from "dayjs";
import { debounce } from "lodash-es";

interface StyledDatePickerProps extends ComponentProps<DatePicker> {
  error?: boolean;
  errorMessage?: string;
  required?: boolean;
  warningText?: string;
  open?: boolean;
  acceptInvalidValue?: boolean;
}

const StyledDatepicker = ({
  id,
  name,
  label,
  placeholder,
  value,
  onChange,
  error,
  errorMessage,
  minDate,
  maxDate,
  disabled,
  readOnly,
  fullWidth,
  required,
  warningText,
  open,
  acceptInvalidValue,
}: StyledDatePickerProps) => {
  const { locale } = useI18n();
  const [openDatepicker, setOpenDatepicker] = useState(open || false);
  const [inputDate, setInputDate] = useState(value ? dayjs(value) : null);

  const debouncedDateChange = useMemo(
    () =>
      debounce((date: Dayjs | null) => {
        if (date && (date.isValid() || acceptInvalidValue)) {
          onChange(date.endOf("day").toDate());
        }
      }, 400),
    [acceptInvalidValue, onChange],
  );

  const handleDateChange = (date: Dayjs | null) => {
    setInputDate(date);
    debouncedDateChange(date);
  };

  useEffect(() => {
    if (open !== undefined) setOpenDatepicker(open);
  }, [open]);

  return !readOnly ? (
    <FormControl
      variant="standard"
      fullWidth={!!fullWidth}
      error={error}
      disabled={disabled}
      required={required}
      data-testid={`${name || id}FormControl`}
    >
      {label ? (
        <InputLabel
          shrink
          htmlFor={`${id || name}Input`}
          sx={{
            color: theme.palette.neutral[600],
            fontSize: 14,
            fontWeight: 600,
            fontFamily: ["Open Sans", "sans-serif"].join(","),
            overflow: "visible",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            transition: "color 0.2s ease-in-out",
            transform: "none",
            ".MuiInputLabel-asterisk": {
              color: theme.palette.error[500],
            },
          }}
          data-testid={`${name || id}Label`}
        >
          {label}
        </InputLabel>
      ) : null}
      <Box
        sx={{
          marginTop: 3,
          ".MuiFormControl-root": {
            width: fullWidth ? "100%" : "auto",
            "&.Mui-error": {
              borderColor: theme.palette.error[600],
              background: theme.palette.error[100],
              color: theme.palette.error[400],

              ".MuiPickersInputBase-root.MuiPickersOutlinedInput-root": {
                color: theme.palette.error[400],
                "&.Mui-focused .MuiPickersOutlinedInput-notchedOutline": {
                  boxShadow: `${alpha(theme.palette.error[600] as string, 0.25)} 0 0 0 0.2rem`,
                  borderColor: theme.palette.error[600],
                  borderRadius: "4px",
                },
                svg: {
                  color: theme.palette.error[400],
                },
              },
            },
          },
          ".MuiPickersInputBase-root.MuiPickersOutlinedInput-root": {
            borderRadius: "4px",
            fontSize: "14px",
            ".MuiPickersOutlinedInput-notchedOutline": {
              borderColor: theme.palette.neutral[200],
            },
          },
          ".MuiPickersInputBase-root.MuiPickersOutlinedInput-root:hover .MuiPickersOutlinedInput-notchedOutline": {
            transition: theme.transitions.create(["border-color", "background-color", "box-shadow"]),
            borderColor: theme.palette.neutral[500],
          },
          ".MuiPickersInputBase-root.MuiPickersOutlinedInput-root.Mui-focused .MuiPickersOutlinedInput-notchedOutline":
            {
              transition: theme.transitions.create(["border-color", "background-color", "box-shadow"]),
              boxShadow: `${alpha(theme.palette.primary.main, 0.25)} 0 0 0 0.2rem`,
              borderColor: theme.palette.primary.main,
              borderWidth: "1px",
            },
          ".MuiPickersInputBase-input.MuiPickersOutlinedInput-input": {
            paddingY: "10px",
            paddingLeft: "16px",
          },
          ".MuiPickersSectionList-root": {
            padding: "10px 0 10px 0",
          },
        }}
        data-testid={name || id}
      >
        <DesktopDatePicker
          className={error ? "Mui-error" : ""}
          value={inputDate}
          onChange={handleDateChange}
          minDate={minDate ? dayjs(minDate).startOf("day").utc() : undefined}
          maxDate={maxDate ? dayjs(maxDate).endOf("day").utc() : undefined}
          slotProps={{
            field: { clearable: true, onClear: () => onChange(undefined) },
            actionBar: {
              actions: ["clear", "today"],
            },
            textField: {
              error: Boolean(error),
              inputProps: {
                name: name,
                placeholder: placeholder,
                id: `${name || id}Input`,
                "data-testid": `${name || id}Input`,
              },
            },
          }}
          disabled={disabled}
          open={openDatepicker}
          onOpen={() => setOpenDatepicker(true)}
          onClose={() => setOpenDatepicker(false)}
        />
      </Box>
      {error && errorMessage ? (
        <FormHelperText data-testid={`${name || id}Error`}>{errorMessage}</FormHelperText>
      ) : warningText ? (
        <FormHelperText
          data-testid={`${name || id}Warning`}
          sx={{
            backgroundColor: theme.palette.warning[100],
            color: theme.palette.warning[800],
            px: 1,
            fontWeight: 500,
            display: "inline-flex",
            alignItems: "center",
            lineHeight: "24px",
            mx: 0,
          }}
          variant="filled"
        >
          <GppMaybeOutlinedIcon fontSize="small" sx={{ color: theme.palette.warning[800], mr: 1 }} />
          {warningText}
        </FormHelperText>
      ) : null}
    </FormControl>
  ) : (
    <StyledInput
      id={id || name}
      fullWidth={!!fullWidth}
      size="small"
      value={value ? dayjs(value).locale(locale).format("L") : ""}
      label={label}
      placeholder={placeholder}
      readOnly
      name={name}
      disabled={disabled}
      testId={name || id}
    />
  );
};

export default StyledDatepicker;
